import type { UpdateResolver } from '@urql/exchange-graphcache'
import type { TypedDocumentNode } from '@urql/next'
import { uniqBy } from 'lodash'
import { GET_MEETING_RECORDINGS } from '@/components/MeetingRoom/MeetingRoomSidepanel/AICapture/Components/Recordings/RecordingsContext'
import { graphql } from '@/generated/gql'
import type {
    AddAttachmentMomMutation,
    AttachmentDeletedSubscriptionVariables,
    AttachmentUpdatedSubscription,
    AttachmentUpdatedSubscriptionVariables,
    CreateAttachmentMutation,
    CreateAttachmentOnActionMutation,
    MutationAddAttachmentMomv1Args,
    MutationCreateAttachmentOnActionV1Args,
    MutationCreateAttachmentV1Args,
    MutationEditAttachmentArgs,
    MutationRemoveAttachmentV1Args,
    MutationRemoveMomv1Args,
    RecordingsUpdatedSubscription,
    RecordingsUpdatedSubscriptionVariables,
    TranscriptionStatus,
} from '@/generated/gql/graphql'
import { AttachmentType, MomType, SortEnumType } from '@/generated/gql/graphql'
import { ATTACHMENTS_BY_MEETING_ID } from '../attachments'
import { GET_MINUTES_REQUESTS } from '../meeting-minutes'

const contentItemTypeToTypename = {
    ACTION: 'Action',
    AGENDA: 'AgendaItemType',
    DECISION: 'Decision',
    MOM: 'MOM',
    NOTE: 'Note',
    POLL: 'Poll',
}

type AttachmentsFragment = TypedDocumentNode<{
    id: string
    attachments: {
        __typename?: 'Attachment'
        id: string
        title: string
        attachmentPath: string
        meetingId?: string
    }[]
}>

const getAttachmentsFragment = (typename: string) => {
    return `
        fragment AttachmentsFragment on ${typename} {
            id
            attachments {
                __typename
                id
                title
                attachmentPath
                meetingId
            }
        }
    ` as unknown as AttachmentsFragment
}

const attachmentUpdaters: Record<string, UpdateResolver> = {
    removeAttachmentV1: (
        _result,
        args: MutationRemoveAttachmentV1Args,
        cache,
    ) => {
        cache.invalidate({
            __typename: 'Attachment',
            id: args.input.id,
        })
    },
    createAttachmentV1: (
        result: CreateAttachmentMutation,
        args: MutationCreateAttachmentV1Args,
        cache,
    ) => {
        const { parentContentType, parentId, ...newAttachment } =
            result.createAttachmentV1.attachment

        if (parentContentType === 'GROUP') {
            cache.invalidate('Query', 'attachments', {
                first: 15,
                order: {
                    title: 'ASC',
                },
                textFilterInput: {},
                where: {
                    and: [
                        {
                            groupIds: {
                                some: {
                                    eq: parentId,
                                },
                            },
                        },
                        {
                            isArchived: {
                                eq: false,
                            },
                        },
                        {
                            attachmentType: {
                                neq: 'MOM',
                            },
                        },
                    ],
                },
            })
        }

        const typename = contentItemTypeToTypename[parentContentType]
        const fragment = getAttachmentsFragment(typename)
        const cachedData = cache.readFragment(fragment, {
            __typename: typename,
            id: parentId,
        })

        if (cachedData != null) {
            cache.writeFragment(fragment, {
                id: parentId,
                attachments: cachedData.attachments.concat(newAttachment),
            })
        }
        cache.updateQuery(
            {
                query: ATTACHMENTS_BY_MEETING_ID,
                variables: {
                    meetingId: args.input.meetingId,
                },
            },
            (data) => {
                if (data == null) return null
                return {
                    ...data,
                    meetingV1: {
                        ...data.meetingV1,
                        meeting: {
                            ...data.meetingV1.meeting,
                            allAttachments: [
                                ...data.meetingV1.meeting.allAttachments,
                                {
                                    id: newAttachment.id,
                                    title: newAttachment.title,
                                    attachmentPath:
                                        newAttachment.attachmentPath,
                                    attachmentType:
                                        newAttachment.attachmentType,
                                    isArchived: false,
                                    size: newAttachment.size,
                                    __typename: 'Attachment' as const,
                                },
                            ],
                        },
                    },
                }
            },
        )
    },
    createAttachmentOnActionV1: (
        result: CreateAttachmentOnActionMutation,
        args: MutationCreateAttachmentOnActionV1Args,
        cache,
    ) => {
        const { parentId, ...newAttachment } =
            result.createAttachmentOnActionV1.attachment
        const typename = contentItemTypeToTypename['ACTION']
        const fragment = getAttachmentsFragment(typename)
        const cachedData = cache.readFragment(fragment, {
            __typename: typename,
            id: parentId,
        })
        if (cachedData != null) {
            cache.writeFragment(fragment, {
                id: parentId,
                attachments: cachedData.attachments.concat(newAttachment),
            })
        }
    },
    addAttachmentMOMV1: (
        result: AddAttachmentMomMutation,
        args: MutationAddAttachmentMomv1Args,
        cache,
    ) => {
        cache.updateQuery(
            {
                query: GET_MINUTES_REQUESTS,
                variables: {
                    first: 20,
                    where: {
                        and: [
                            { meetingId: { eq: args.input.id } },
                            { isArchived: { eq: false } },
                            {
                                type: {
                                    eq: MomType.Attachment,
                                },
                            },
                        ],
                    },
                },
            },
            (data) => {
                if (data == null) return null
                return {
                    ...data,
                    moms: {
                        ...data.moms,
                        totalCount: data.moms.totalCount + 1,
                        nodes: [
                            ...data.moms.nodes,
                            result.addAttachmentMOMV1.mom,
                        ],
                    },
                }
            },
        )
    },
    removeMOMV1: (_result, args: MutationRemoveMomv1Args, cache) => {
        cache.invalidate({
            __typename: 'MOM',
            id: args.input.momId,
        })
    },
    editAttachment: (_result, args: MutationEditAttachmentArgs, cache) => {
        cache.invalidate({
            __typename: 'Attachment',
            id: args.editAttachmentInput.id,
        })
    },
}

const attachmentsSubscriptionsUpdaters: Record<string, UpdateResolver> = {
    recordingsUpdated: (
        result: RecordingsUpdatedSubscription,
        args: RecordingsUpdatedSubscriptionVariables,
        cache,
    ) => {
        const { recordings } = result.recordingsUpdated
        const { meetingId } = args
        const variables = {
            input: {
                meetingId,
            },
            order: {
                order: SortEnumType.Asc,
            },
        }
        cache.updateQuery(
            {
                query: GET_MEETING_RECORDINGS,
                variables,
            },
            (data) => {
                if (data != null) {
                    const mappedRecordings = recordings.map((recording) => {
                        return {
                            ...recording,
                            __typename: 'Attachment' as const,
                            meetingId,
                            transcriptionStatus:
                                recording.transcriptionStatus as unknown as TranscriptionStatus,
                        }
                    })

                    data.recordings = mappedRecordings
                    return data
                }
                return null
            },
        )
        cache.updateQuery(
            {
                query: ATTACHMENTS_BY_MEETING_ID,
                variables: { meetingId },
            },
            (data) => {
                if (data == null) return null

                const oldRecordings =
                    data.meetingV1.meeting.allAttachments.filter(
                        (a) => a.attachmentType === AttachmentType.Recording,
                    )

                const updatedRecordings = recordings.map((r) => ({
                    id: r.id,
                    title: r.title,
                    attachmentPath: r.attachmentPath,
                    attachmentType: AttachmentType.Recording,
                    isArchived: false,
                    size: r.size,
                    __typename: 'Attachment' as const,
                }))

                // Get recordings that exist in old but not in updated (removed)
                const removedRecordingsIds = oldRecordings
                    .filter(
                        (r) => !updatedRecordings.some((u) => u.id === r.id),
                    )
                    .map((r) => r.id)

                // Filter out removed recordings and add new ones
                const updatedAllAttachments = uniqBy(
                    [
                        ...data.meetingV1.meeting.allAttachments.filter(
                            (a) => !removedRecordingsIds.includes(a.id),
                        ),
                        ...updatedRecordings,
                    ],
                    'id',
                )

                return {
                    ...data,
                    meetingV1: {
                        ...data.meetingV1,
                        meeting: {
                            ...data.meetingV1.meeting,
                            allAttachments: updatedAllAttachments,
                        },
                    },
                }
            },
        )
    },
    attachmentUpdated: (
        result: AttachmentUpdatedSubscription,
        args: AttachmentUpdatedSubscriptionVariables,
        cache,
    ) => {
        const {
            attachment: { title, attachmentPath, id: noteId },
        } = result.attachmentUpdated
        cache.writeFragment(
            graphql(`
              fragment AttachmentUpdatedFragment on Attachment {
                id
                title
                attachmentPath
              }
            `),
            { id: noteId, title, attachmentPath },
        )
    },
    attachmentDeleted: (
        _result,
        args: AttachmentDeletedSubscriptionVariables,
        cache,
    ) => {
        cache.invalidate({
            __typename: 'Attachment',
            id: args.attachmentId,
        })
    },
}

export { attachmentUpdaters, attachmentsSubscriptionsUpdaters }
